import { USER_TYPE } from "@mozioinc/query";
import {
  Button,
  Header,
  HeaderContent,
  HeaderButton,
  HeaderLogo,
  HeaderCombobox,
  HeaderSignInButton,
  HeaderCreateAccountButton,
  HeaderAvatar,
  HeaderUserAvatar,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetClose,
  cn,
} from "@mozioinc/ui";
import {
  addPreservedParams,
  resolveDomainTokenUrl,
  url,
} from "@mozioinc/utils";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import MyTripsSvg from "../components/assets/my-trips.svg?react";
import PassengerSvg from "../components/assets/passenger.svg?react";
import SignOutSvg from "../components/assets/sign-out.svg?react";
import TimesSvg from "../components/assets/times.svg?react";
import { MobileAppDownloadBanner } from "../components/mobile-app-download-banner";
import { MobileAppDownloadButton } from "../components/mobile-app-download-button";
import { NavigationMenu } from "../components/navigation-menu";
import { PreservedLink } from "../components/preserved-link";
import { ResendBookingDialog } from "../components/resend-booking-dialog";
import { useCurrency, useLogout } from "../hooks";
import { useShowMobileAppDownload } from "../hooks/use-show-mobile-app-download";
import { formatString } from "../utils/format-string";
import {
  languageToOldFormat,
  languages as supportedLanguages,
  replaceLocaleInPath,
} from "../utils/languages";
import { captureLanguageChanged } from "../utils/posthog";
import { currencyQuery } from "../utils/query";
import { getWhiteLabel } from "../utils/white-label";
import type { TransNamespace } from "../types";
import type { User } from "@mozioinc/query";

const supportedLocales = new Set(supportedLanguages.map((lang) => lang.locale));

const UserAccountButtons = () => {
  const { t, i18n } = useTranslation<TransNamespace>("transfer:header");
  const { header, tuiCSAdjustments } = getWhiteLabel();
  const logout = useLogout();
  const fromPath = encodeURIComponent(window.location.href);

  return (
    <>
      {header.showMyBookings && (
        <HeaderButton
          asChild
          className="bg-white text-neutral-800 hover:bg-neutral-100 hover:text-neutral-800"
        >
          <PreservedLink
            href={`/v2/user/my-reservations?locale=${i18n.language}&from_path=${fromPath}`}
          >
            <MyTripsSvg className="text-xl" />
            {t("My bookings")}
          </PreservedLink>
        </HeaderButton>
      )}

      {header.showMyAccount && (
        <HeaderButton
          asChild
          className="bg-white text-neutral-800 hover:bg-neutral-100 hover:text-neutral-800"
        >
          <PreservedLink
            href={`/v2/user/info?locale=${i18n.language}&from_path=${fromPath}`}
          >
            <PassengerSvg className="text-xl" />
            {t("My account")}
          </PreservedLink>
        </HeaderButton>
      )}

      {!tuiCSAdjustments && (
        <HeaderButton
          className="w-full bg-white text-error-300 hover:bg-neutral-100 hover:text-error-300"
          onClick={logout}
        >
          <SignOutSvg className="text-xl" />
          {t("Log out")}
        </HeaderButton>
      )}
    </>
  );
};

type HeaderContainer = {
  user?: User;
};

const HeaderContainer = ({ user }: HeaderContainer) => {
  const { i18n } = useTranslation();
  const { t } = useTranslation<TransNamespace>("transfer:header");
  const { header, showResendBooking } = getWhiteLabel();
  const logoUrl =
    formatString(resolveDomainTokenUrl(header.customLogoLink)) || "/"; // TODO: We'll need to map if it's comming from esim/parking too
  const [openResendDialog, setOpenResendDialog] = useState(false);
  const isSignedIn = !!user;

  const [sheetOpen, setSheetOpen] = useState(false);
  const { isBannerShown } = useShowMobileAppDownload();

  const getLabel = (language: { name: string; englishName: string }) => {
    return i18n.language === "en_US"
      ? `${language.name} (${language.englishName})`
      : language.name;
  };

  const languages = supportedLanguages.map((l) => ({
    ...l,
    label: getLabel(l),
    value: getLabel(l).toLowerCase(),
  }));

  const currentLanguage =
    languages.find((l) => l.locale === languageToOldFormat(i18n.language)) ??
    languages[0]!;

  const { currency } = useCurrency();

  const [currencyMenuOpen, setCurrencyMenuOpen] = useState(false);

  const currenciesQuery = useQuery({
    queryKey: ["currencies", currencyMenuOpen, i18n.language],
    queryFn: currencyQuery.getCurrencies,
    enabled: currencyMenuOpen,
  });

  const priorityCurrencyOrder = ["USD", "GBP", "EUR"];

  const currencies =
    currenciesQuery.data?.map((c) => ({
      ...c,
      href: url.setParam("currency", c.code),
      value: `${c.code} - ${c.name}`.toLowerCase(),
      label: `${c.code} - ${c.name}`,
    })) || [];

  currencies.sort((a, b) => {
    const aPriority = priorityCurrencyOrder.indexOf(a.code);
    const bPriority = priorityCurrencyOrder.indexOf(b.code);
    if (aPriority !== -1 || bPriority !== -1) {
      return (
        (aPriority === -1 ? priorityCurrencyOrder.length : aPriority) -
        (bPriority === -1 ? priorityCurrencyOrder.length : bPriority)
      );
    }
    return a.code.localeCompare(b.code);
  });

  const currentCurrency =
    currencies.find((c) => c.code === currency) ?? currencies[0];

  const currentCurrencyCode = currentCurrency?.code || currency;

  const isTravelAgent = user?.userType === USER_TYPE.TRAVEL_AGENT;
  const showTravelAgentSubtext = isTravelAgent && header.headerShowTaSubtext;
  const travelAgentSubtext = showTravelAgentSubtext
    ? t("Travel Agent")
    : undefined;

  const links = header?.customLinks
    ?.map((link) => [formatString(link[0]), formatString(link[1])])
    .filter((link) => link[0] && link[1]);

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [headerOpen, setHeaderOpen] = useState(false);

  const handleLocaleChange = (value: string) => {
    const language = languages.find((l) => l.value === value)!;

    if (user) {
      captureLanguageChanged({
        userDataLocale: user.locale,
        newLocale: language.locale,
      });
    }

    const url = new URL(window.location.href);
    const newPathname = replaceLocaleInPath(
      url.pathname,
      language.locale,
      supportedLocales,
    );

    if (newPathname !== undefined) {
      url.pathname = newPathname;
      window.location.href = url.toString();
    }
  };

  return (
    <nav
      className={cn(
        "fixed inset-x-0 top-0 z-30 h-[var(--mz-header-height)]",
        isBannerShown && "h-[calc(var(--mz-header-height)*2)]",
      )}
    >
      <MobileAppDownloadBanner />
      <Header className={cn(isBannerShown && "top-[var(--mz-header-height)]")}>
        <HeaderLogo href={addPreservedParams(logoUrl)}>
          <img src={header.logo} alt="logo" />
        </HeaderLogo>
        <MobileAppDownloadButton />
        {isSignedIn && (
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <HeaderButton className="ml-auto px-0 lg:hidden" asChild>
              <SheetTrigger>
                <HeaderAvatar
                  label={user?.firstName[0]}
                  showCaret
                  isOpen={sheetOpen}
                />
              </SheetTrigger>
            </HeaderButton>

            <SheetContent
              side={"bottom"}
              className="z-50 mt-[var(--mz-header-height)] flex flex-col gap-3 rounded-t-lg p-3"
            >
              <div className="flex justify-between px-4">
                <h2 className="py-2 text-lg font-bold">
                  {t("Hello, {{name}}!", { name: user.firstName })}
                </h2>
                <SheetClose>
                  <TimesSvg className="text-xl text-primary" />
                </SheetClose>
              </div>

              <NavigationMenu
                onClose={() => {
                  setSheetOpen(false);
                }}
              />
            </SheetContent>
          </Sheet>
        )}

        {header.showSignInButton && !isSignedIn && (
          <HeaderButton className="ml-auto px-0 lg:hidden" asChild>
            <PreservedLink
              href={`/${languageToOldFormat(i18n.language)}/user/login?returnPath=${encodeURIComponent(window.location.pathname + window.location.search)}`}
            >
              <HeaderAvatar />
            </PreservedLink>
          </HeaderButton>
        )}

        <HeaderContent open={headerOpen} onOpenChange={setHeaderOpen}>
          <div className="flex justify-between px-4 lg:hidden">
            <h2 className="py-2 text-lg font-bold">{t("More")}</h2>
            <Button
              variant={"ghost"}
              className="px-0"
              onClick={() => {
                setHeaderOpen(false);
              }}
            >
              <TimesSvg className="text-xl text-primary" />
            </Button>
          </div>

          <div className="my-3 flex flex-col items-start gap-4 lg:my-0 lg:flex-row lg:gap-0">
            {links?.map((link) => (
              <HeaderButton key={link[1]} asChild>
                <a href={link[1]} target="_blank" rel="noreferrer">
                  {link[0]}
                </a>
              </HeaderButton>
            ))}

            {showResendBooking && (
              <HeaderButton asChild>
                <Button
                  variant={"solid"}
                  onClick={() => {
                    setOpenResendDialog(true);
                  }}
                >
                  {t("Resend Booking")}
                </Button>
              </HeaderButton>
            )}

            <HeaderCombobox
              value={currentLanguage.value}
              triggerText={currentLanguage.name}
              label="Language"
              items={languages}
              shouldFilter
              onValueChange={handleLocaleChange}
              noResultsText={t("No results found")}
            />

            <HeaderCombobox
              value={currentCurrency?.value}
              triggerText={currentCurrencyCode}
              label="Currency"
              items={currencies}
              open={currencyMenuOpen}
              loading={currenciesQuery.isFetching}
              shouldFilter
              onValueChange={(value) => {
                window.location.href = currencies.find(
                  (l) => l.value === value,
                )!.href;
              }}
              onOpenChange={setCurrencyMenuOpen}
              noResultsText={t("No results found")}
            />

            {header.showSignInButton && !isSignedIn && (
              <>
                <HeaderSignInButton asChild>
                  <PreservedLink
                    href={`/${languageToOldFormat(i18n.language)}/user/login?returnPath=${encodeURIComponent(window.location.pathname + window.location.search)}`}
                  >
                    {t("Sign In")}
                  </PreservedLink>
                </HeaderSignInButton>

                <div className="w-full px-4 lg:w-auto">
                  <HeaderCreateAccountButton asChild className="w-full">
                    <PreservedLink
                      href={`/${languageToOldFormat(
                        i18n.language,
                      )}/user/register`}
                    >
                      {t("Create Account")}
                    </PreservedLink>
                  </HeaderCreateAccountButton>
                </div>
              </>
            )}

            {isSignedIn && (
              <div className="hidden lg:flex">
                <Popover onOpenChange={setPopoverOpen}>
                  <PopoverTrigger asChild>
                    <HeaderButton className="hidden lg:flex">
                      <HeaderUserAvatar
                        name={`${user?.firstName} ${user?.lastName}`}
                        email={user?.email}
                        role={travelAgentSubtext}
                        isOpen={popoverOpen}
                        displayField={header.headerUsername}
                      />
                    </HeaderButton>
                  </PopoverTrigger>
                  <PopoverContent
                    sideOffset={12}
                    align="end"
                    className="ml-auto w-44 px-0"
                  >
                    <UserAccountButtons />
                  </PopoverContent>
                </Popover>
              </div>
            )}
          </div>
        </HeaderContent>

        <ResendBookingDialog
          open={openResendDialog}
          onClose={() => {
            setOpenResendDialog(false);
          }}
        />
      </Header>
    </nav>
  );
};

export { HeaderContainer as Header };
